import React, { useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import get from 'lodash.get';

import Header from '../../components/global/Header';
import Footer from '../../components/global/Footer';
import PhotoGrid from '../../components/grid/PhotoGrid';
import ProgressivePhoto from '../../components/grid/ProgressivePhoto';
import WaldoProductLogo from '../../components/global/WaldoProductLogo';
import useShiftSelect from '../../hooks/useShiftSelect';

const propTypes = {
  loading: PropTypes.bool,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      thumbnail_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          width: PropTypes.number,
          height: PropTypes.number,
        }),
      ),
    }),
  ),
  total: PropTypes.number,
  loadMore: PropTypes.func.isRequired,
};

const MultiSelectView = ({ loading, photos, total, loadMore }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const isSelected = photoId => selectedIds.includes(photoId);

  const onSelectPhotos = photosToSelect =>
    setSelectedIds(selectedIdsCurrent => [
      ...selectedIdsCurrent,
      ...photosToSelect.map(({ id }) => id),
    ]);

  const onDeselectPhotos = photosToDeselect => {
    const photoIds = photosToDeselect.map(({ id }) => id);
    setSelectedIds(selectedIdsCurrent =>
      selectedIdsCurrent.filter(photoId => !photoIds.includes(photoId)),
    );
  };

  const { onToggleSelectedItem } = useShiftSelect(
    photos,
    onSelectPhotos,
    onDeselectPhotos,
  );

  return (
    <div className="wrapper-main">
      <Header>
        <WaldoProductLogo type="rnd" withIcon brandColor />
      </Header>

      <div
        className="d-flex flex-column"
        style={{
          width: '100%',
        }}
      >
        <PhotoGrid
          loading={loading}
          records={photos}
          total={total}
          loadMore={loadMore}
          cellRenderer={({ record: photo }) => {
            return (
              <button
                className="btn-reset"
                onClick={() => {
                  onToggleSelectedItem(photo, isSelected(photo.id));
                }}
              >
                <ProgressivePhoto
                  progressive
                  className={isSelected(photo.id) ? 'avatar-selected' : ''}
                  thumbnails={photo.thumbnail_urls}
                />
              </button>
            );
          }}
        />
        )
      </div>

      <Footer />
    </div>
  );
};

MultiSelectView.propTypes = propTypes;

const Album = gql`
  query album($id: RecordID!, $limit: Int!, $offset: Int!) {
    album(id: $id) {
      id
      photos(slice: { limit: $limit, offset: $offset }) {
        total
        count
        records {
          id
          thumbnail_urls {
            url
            width
            height
            size_code
          }
        }
      }
    }
  }
`;

const withAlbumPhotos = graphql(Album, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
      limit: 100,
      offset: 0,
    },
  }),
  props: ({ data: { album, loading, fetchMore } }) => ({
    loading,
    photos: get(album, 'photos.records'),
    total: get(album, 'photos.total'),
    loadMore() {
      return fetchMore({
        variables: {
          offset: album.photos.count,
          limit: 100,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult;
          }

          const prevCount = previousResult.album.photos.count;
          const nextCount = fetchMoreResult.album.photos.count;

          return {
            ...previousResult,
            album: {
              ...previousResult.album,
              photos: {
                ...previousResult.album.photos,
                count: prevCount + nextCount,
                records: [
                  ...previousResult.album.photos.records,
                  ...fetchMoreResult.album.photos.records,
                ],
              },
            },
          };
        },
      });
    },
  }),
});

export default compose(withRouter, withAlbumPhotos)(MultiSelectView);
